import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';

import getConfig from '../../../../util/config';
import { gtm } from '../../../../util/analytics';
import { MeQuery } from '../../../../graphql/queries/account';
import useIsNativeWebView from '../../../../services/native-bridge/hooks/useIsWebView';

/**
 * Formats a string for appending to a url.
 *
 * @param {string} [fragment] The url fragment.
 * @returns {string}
 */
const formatUrlFragment = fragment => {
  if (!fragment) {
    return '';
  }

  return fragment.replace(/\s+/g, '-').toLowerCase();
};

/**
 * Fires a virtual page view.
 *
 * @param {Object} options The options.
 * @param {string} [options.title] The page title.
 * @param {string} [options.path] The page path.
 * @param {boolean} [options.fireOnMount] Whether the page view should fire on first render.
 * @returns {{onVirtualPageViewTrack: (function({pageViewTitle?: *, pageViewPath?: *}): (undefined))}}
 */
const useVirtualPageView = ({ title, path, fireOnMount = true }) => {
  const location = useLocation();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const isNativeWebView = useIsNativeWebView();

  const config = getConfig();
  const { baseUrl } = config?.app ?? {};

  const onVirtualPageViewTrack = ({ pageViewTitle, pageViewPath }) => {
    if (isNativeWebView) {
      return;
    }

    if (!pageViewPath) {
      throw Error('useVirtualPageView: `path` is a required parameter.');
    }

    gtm.trackPageView({
      url: `${baseUrl}${location.pathname}/v${formatUrlFragment(pageViewPath)}`,
      title: pageViewTitle,
      userId: user?.id ?? undefined,
    });
  };

  useEffect(() => {
    if (fireOnMount) {
      onVirtualPageViewTrack({
        pageViewTitle: title,
        pageViewPath: path,
      });
    }
  }, []);

  return {
    onVirtualPageViewTrack,
  };
};

export default useVirtualPageView;
